import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Users } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

/** Visitatori unici + pageview dal tracking interno: oggi / 7gg / 30gg / anno. */
export const VisitorStatsWidget = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = sessionStorage.getItem("access_token");
        const res = await axios.get(`${BACKEND_URL}/api/dashboard/visitor-stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setStats(res.data);
      } catch (e) {
        console.warn("[visitors] fetch failed:", e?.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const box = (label, visitors, pageviews, cls, testid, trend, vs) => {
    const up = trend != null && trend >= 0;
    return (
      <div className="text-center p-4 bg-gray-50 rounded-lg" data-testid={testid}>
        <div className={`text-3xl font-extrabold ${cls}`}>{visitors ?? "—"}</div>
        <div className="text-[11px] uppercase tracking-wide text-gray-500 mt-1">{label}</div>
        <div className="text-[11px] text-gray-400 mt-1" data-testid={`${testid}-pageviews`}>
          {pageviews != null ? `${pageviews} pagine viste` : ""}
        </div>
        <div
          className={`text-[11px] font-bold mt-1 ${trend == null ? "text-gray-400" : up ? "text-green-600" : "text-red-500"}`}
          data-testid={`${testid}-trend`}
        >
          {trend == null ? "—" : `${up ? "▲ +" : "▼ "}${trend}%`}{" "}
          <span className="font-normal text-gray-400">{vs}</span>
        </div>
      </div>
    );
  };

  return (
    <Card data-testid="visitor-stats-widget">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-500" />
          Visitatori del Sito
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-gray-400 text-sm text-center py-4">Caricamento…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {box("Oggi", stats?.daily, stats?.daily_pageviews, "text-blue-600", "visitors-daily", stats?.trend_daily, "vs ieri")}
              {box("Ultimi 7 giorni", stats?.weekly, stats?.weekly_pageviews, "text-indigo-600", "visitors-weekly", stats?.trend_weekly, "vs 7gg prec.")}
              {box("Ultimi 30 giorni", stats?.monthly, stats?.monthly_pageviews, "text-purple-600", "visitors-monthly", stats?.trend_monthly, "vs 30gg prec.")}
              {box("Ultimi 12 mesi", stats?.yearly, stats?.yearly_pageviews, "text-pink-600", "visitors-yearly", stats?.trend_yearly, "vs anno prec.")}
            </div>
            {Array.isArray(stats?.series) && stats.series.length > 0 && (() => {
              const max = Math.max(...stats.series.map((d) => d.visitors), 1);
              const fmt = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
              return (
                <div className="mt-4" data-testid="visitors-chart">
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-2">
                    Andamento ultimi 30 giorni
                  </div>
                  <div className="flex items-end gap-[3px] h-24 bg-gray-50 rounded-lg p-2">
                    {stats.series.map((d) => (
                      <div
                        key={d.date}
                        className="flex-1 bg-blue-500 hover:bg-pink-500 transition-colors rounded-t-sm min-w-[3px]"
                        style={{ height: `${Math.max((d.visitors / max) * 100, d.visitors > 0 ? 6 : 2)}%`, opacity: d.visitors > 0 ? 1 : 0.25 }}
                        title={`${fmt(d.date)}: ${d.visitors} visitator${d.visitors === 1 ? "e" : "i"}`}
                        data-testid={`visitors-bar-${d.date}`}
                      />
                    ))}
                  </div>
                  <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                    <span>{fmt(stats.series[0].date)}</span>
                    <span>{fmt(stats.series[stats.series.length - 1].date)}</span>
                  </div>
                </div>
              );
            })()}
            <p className="text-[11px] text-gray-400 mt-3">
              Visitatori unici (per sessione/dispositivo) misurati dal tracking interno del sito.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};
